import type { ApiResponse, ApiInstruction, ProductData } from "./documentSearch/model/InstructionApi";
import type { Instruction, TransformedProduct, ValidationResult } from "./documentSearch/model/InstructionsProduct";

const API_URL = "https://api.ehrenkind.de/v1/shop/instructions/search";

/** Download icon matching snippets/ek-instruction-file-link.liquid */
const DOWNLOAD_ICON =
  '<svg class="shrink-0" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>';

const SAFE_PROTOCOLS = ["http:", "https:"];

/** Returns a safe href value (http/https only), or "#" if the URL is invalid or uses an unsafe protocol. */
function sanitizeUrlForHref(url: string): string {
  try {
    const parsed = new URL(url, typeof window !== "undefined" ? window.location.origin : "https://www.ehrenkind.de");
    if (SAFE_PROTOCOLS.includes(parsed.protocol)) {
      return parsed.href;
    }
  } catch {
    /* invalid URL */
  }
  return "#";
}

/** Builds file link HTML matching snippet styling (margin_class: mt-4 first, mt-2 rest) */
function buildFileLinkHtml(url: string, label: string, index = 0): string {
  const safeUrl = sanitizeUrlForHref(url);
  const escapedUrl = safeUrl.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const escapedLabel = label.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  return `<a href="${escapedUrl}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-2 my-2 px-3 ${index === 0 ? "pt-2" : ""} pb-2 text-sm font-medium !no-underline border border-gray-300 rounded-md hover:border-gray-400 bg-lightgray hover:bg-separatorgray hover:text-black transition-all duration-150 break-all" download>${DOWNLOAD_ICON}${escapedLabel}</a>`;
}

class EkDocumentSearch {
  private sectionId: string;

  private searchForm: HTMLFormElement | null;
  private orderNumberInput: HTMLInputElement | null;
  private zipCodeInput: HTMLInputElement | null;
  private searchButton: HTMLButtonElement | null;
  private loadingState: HTMLElement | null;
  private errorState400: HTMLElement | null;
  private errorState500: HTMLElement | null;
  private resultsContainer: HTMLElement | null;

  constructor(sectionId: string) {
    this.sectionId = sectionId;

    this.searchForm = document.getElementById(`SearchForm-${sectionId}`) as HTMLFormElement;
    this.orderNumberInput = document.getElementById(`OrderNumberInput-${sectionId}`) as HTMLInputElement;
    this.zipCodeInput = document.getElementById(`ZipCodeInput-${sectionId}`) as HTMLInputElement;
    this.searchButton = document.getElementById(`SearchButton-${sectionId}`) as HTMLButtonElement;
    this.loadingState = document.getElementById(`LoadingState-${sectionId}`);
    this.errorState400 = document.getElementById(`ErrorState-400-${sectionId}`);
    this.errorState500 = document.getElementById(`ErrorState-500-${sectionId}`);
    this.resultsContainer = document.getElementById(`ResultsContainer-${sectionId}`);

    this.init();
  }

  private init(): void {
    if (!this.searchForm || !this.orderNumberInput || !this.zipCodeInput) {
      return;
    }

    // Add input listeners for real-time validation and border styling
    this.orderNumberInput.addEventListener("input", () => {
      this.validateForm();
      this.updateInputBorder(this.orderNumberInput);
    });
    this.zipCodeInput.addEventListener("input", () => {
      this.validateForm();
      this.updateInputBorder(this.zipCodeInput);
    });

    this.searchForm.addEventListener("submit", (e: SubmitEvent) => {
      e.preventDefault();
      const orderNumber = this.orderNumberInput?.value.trim();
      const zipCode = this.zipCodeInput?.value.trim();
      if (orderNumber && zipCode) {
        this.validateAndSearch(orderNumber, zipCode);
      }
    });
  }

  private validateForm(): void {
    const orderNumber = this.orderNumberInput?.value.trim() || "";
    const zipCode = this.zipCodeInput?.value.trim() || "";
    const isValid = orderNumber.length > 0 && zipCode.length > 0;

    if (this.searchButton) {
      this.searchButton.disabled = !isValid;
      if (isValid) {
        this.searchButton.classList.remove("button-gray-disabled");
        this.searchButton.classList.add("!button-black");
      } else {
        this.searchButton.classList.add("button-gray-disabled");
        this.searchButton.classList.remove("!button-black");
      }
    }
  }

  private updateInputBorder(input: HTMLInputElement | null): void {
    if (!input) return;
    const value = input.value.trim();
    if (value.length > 0) {
      input.classList.add("filled");
    } else {
      input.classList.remove("filled");
    }
  }

  private async validateOrder(orderNumber: string, zipCode: string): Promise<ValidationResult> {
    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          orderNumber: orderNumber.trim(),
          zipCode: zipCode.trim(),
        }),
      });

      if (!response.ok) {
        if (response.status === 404) {
          return { valid: false, errorType: "not_found" };
        }
        if (response.status === 500) {
          return { valid: false, errorType: "server_error" };
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ApiResponse = await response.json();
      const responseData = data.response || data;

      if (!responseData.success) {
        return { valid: false, errorType: "not_found" };
      }

      const products = (responseData.products || []).map((product: ProductData) => {
        const instructions: Instruction[] = [];

        if (product.instructions && product.instructions.length > 0) {
          product.instructions.forEach((item: ApiInstruction) => {
            const type = item.type === "assembly_instructions" ? "assembly" : "manual";
            const label = type === "assembly" ? "Montageanleitung" : "Gebrauchsanweisung";
            instructions.push({
              label,
              url: item.url,
              type,
              fileName: item.fileName,
              versionDate: item.versionDate,
            });
          });
        } else {
          if (product.assemblyInstructions?.length) {
            product.assemblyInstructions.forEach((url: string) => {
              instructions.push({ label: "Montageanleitung", url, type: "assembly" });
            });
          }
          if (product.instructionManuals?.length) {
            product.instructionManuals.forEach((url: string) => {
              instructions.push({ label: "Gebrauchsanweisung", url, type: "manual" });
            });
          }
        }

        return {
          title: product.title,
          sku: product.sku,
          imageUrl: product.imageUrl,
          instructions,
        };
      });

      return { valid: true, products };
    } catch {
      return { valid: false, errorType: "api_error" };
    }
  }

  private async validateAndSearch(orderNumber: string, zipCode: string): Promise<void> {
    this.loadingState?.classList.remove("hidden");
    this.errorState400?.classList.add("hidden");
    this.errorState500?.classList.add("hidden");
    if (this.resultsContainer) {
      this.resultsContainer.innerHTML = "";
    }

    if (this.orderNumberInput) {
      this.orderNumberInput.disabled = true;
    }
    if (this.zipCodeInput) {
      this.zipCodeInput.disabled = true;
    }
    if (this.searchButton) {
      this.searchButton.disabled = true;
    }

    try {
      const data = await this.validateOrder(orderNumber, zipCode);

      if (data.valid && data.products) {
        this.renderResults(data.products);
      } else {
        if (data.errorType === "not_found") this.errorState400?.classList.remove("hidden");
        else this.errorState500?.classList.remove("hidden");
      }
    } catch {
      this.errorState500?.classList.remove("hidden");
    } finally {
      this.loadingState?.classList.add("hidden");
      if (this.orderNumberInput) {
        this.orderNumberInput.disabled = false;
      }
      if (this.zipCodeInput) {
        this.zipCodeInput.disabled = false;
      }
      // Re-validate to set button state
      this.validateForm();
    }
  }

  /** Group instructions by version_date (newest first). Missing date = '' = current. */
  private groupInstructionsByDate(instructions: Instruction[]): { date: string; instructions: Instruction[] }[] {
    const map = new Map<string, Instruction[]>();
    for (const i of instructions) {
      const key = i.versionDate ?? "";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(i);
    }
    const keys = Array.from(map.keys()).sort((a, b) => {
      if (a === "") return -1;
      if (b === "") return 1;
      return b.localeCompare(a);
    });
    return keys.map((date) => ({ date, instructions: map.get(date)! }));
  }

  /** Render date groups: first group = current; rest under one "Ältere Dokumentversionen" section with date subheadings. */
  private renderInstructionsByDateGroups(groups: { date: string; instructions: Instruction[] }[]): string {
    const currentGroup = groups[0];
    const olderGroups = groups.slice(1);
    const renderBlock = (
      group: { date: string; instructions: Instruction[] },
      nextGroupDate: string,
      renderDate: boolean,
      isFirstInSection: boolean,
      firstHeadingMt: "mt-0" | "mt-4" = "mt-4",
    ) => {
      const manualList = group.instructions.filter((i) => i.type === "manual");
      const assemblyList = group.instructions.filter((i) => i.type === "assembly");
      if (manualList.length === 0 && assemblyList.length === 0) return "";
      const manualHtml =
        manualList.length > 0
          ? `<p class="text-black text-base font-semibold leading-[155%] tracking-[0.16px] mb-[2px] ${firstHeadingMt}">Gebrauchsanweisung:</p><div class="flex flex-col gap-1">${manualList
              .map((inst) => buildFileLinkHtml(inst.url, inst.fileName ?? "Gebrauchsanweisung.pdf"))
              .join("")}</div>`
          : "";
      const assemblyHtml =
        assemblyList.length > 0
          ? `<p class="text-black text-base font-semibold leading-[155%] tracking-[0.16px] mb-[2px] mt-4">Montageanleitung:</p><div class="flex flex-col gap-1">${assemblyList
              .map((inst) => buildFileLinkHtml(inst.url, inst.fileName ?? "Montageanleitung.pdf"))
              .join("")}</div>`
          : "";
      const endDate = this.formatVersionDate(nextGroupDate);
      const dateLabel = group.date ? this.formatVersionDate(group.date) : "";
      const dateHeading = renderDate && dateLabel ? `<p class="text-sm font-medium text-secondary mt-2">${this.escapeHtml(dateLabel)}${endDate ? ` bis ${endDate}` : ""}</p>` : "";
      return `${dateHeading}${manualHtml}${assemblyHtml}`;
    };
    const currentHtml = currentGroup ? renderBlock(currentGroup, "", false, false, "mt-0") : "";
    const olderHtml =
      olderGroups.length > 0
        ? `<p class="text-black text-base font-semibold leading-[155%] tracking-[0.16px] mb-[2px] mt-6 border-t border-gray-200 pt-4">Ältere Dokumentversionen</p>${olderGroups
            .map((group, i) => renderBlock(group, i === 0 ? (currentGroup.date ?? "") : (olderGroups[i - 1].date ?? ""), true, i === 0, "mt-4"))
            .filter(Boolean)
            .join("")}`
        : "";
    return `${currentHtml}<div class="mt-6">${olderHtml}</div>`;
  }

  private formatVersionDate(isoDate: string): string {
    const d = new Date(isoDate);
    if (Number.isNaN(d.getTime())) return isoDate;
    return d.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "UTC",
    });
  }

  private renderResults(products: TransformedProduct[]): void {
    if (!products || products.length === 0) {
      if (this.resultsContainer) {
        this.resultsContainer.innerHTML = `
          <div class="text-center py-12 max-w-md mx-auto">
            <div class="flex justify-center mb-4">
              <svg class="h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">Keine Produkte gefunden</h3>
            <p class="text-gray-600">
              Für diese Bestellung wurden keine Anleitungen gefunden.
              <br>
              Bitte kontaktiere den Support.
            </p>
          </div>
        `;
      }
      return;
    }

    const resultsHtml = products
      .map((product) => {
        const instructionsByDate = this.groupInstructionsByDate(product.instructions);
        const instructionsBlocksHtml = this.renderInstructionsByDateGroups(instructionsByDate);
        return `
          <div class="bg-white shadow rounded p-5">
            <div class="flex gap-4 mb-4">
              <img
                src="${this.escapeHtml(product.imageUrl)}"
                alt="${this.escapeHtml(product.title)}"
                class="w-20 h-[86px] object-contain shrink-0"
                loading="lazy"
              >
              <h3 class="text-base font-semibold text-gray-900 leading-tight">
                ${this.formatProductTitle(product.title)}
              </h3>
            </div>
            <div>
              ${instructionsBlocksHtml || `<p class="text-gray-500 text-sm py-2">Keine Anleitungen verfügbar</p>`}
            </div>
          </div>
        `;
      })
      .join("");

    if (this.resultsContainer) {
      this.resultsContainer.innerHTML = `
        <div class="mb-6 md:mb-[34px] text-center">
          <p class="text-sm font-medium text-gray-900 uppercase tracking-wide">
            ${products.length} ${products.length === 1 ? "PRODUKT" : "PRODUKTE"} GEFUNDEN
          </p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-[22px]">
          ${resultsHtml}
        </div>
      `;
    }
  }

  private escapeHtml(text: string): string {
    const map: Record<string, string> = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#039;",
    };
    return String(text).replace(/[&<>"']/g, (m) => map[m] || m);
  }

  private formatProductTitle(title: string): string {
    // Escape HTML first
    const escaped = this.escapeHtml(title);
    // Replace only the first pipe with <br>
    return escaped.replace(/\|/, "<br>");
  }
}

export function initEkDocumentSearch(root: ParentNode = document): void {
  const sections = root.querySelectorAll<HTMLElement>("[data-document-search]");

  if (sections.length === 0) return;

  sections.forEach((section) => {
    const sectionId = section.dataset.documentSearch;
    if (!sectionId) return;

    if (section.dataset.documentSearchInit === "true") {
      return;
    }
    section.dataset.documentSearchInit = "true";

    try {
      new EkDocumentSearch(sectionId);
    } catch {
      /* section init failed */
    }
  });
}
